import React, { useCallback } from 'react';
import {
  Card, CardContent, CardHeader, Typography, Grid, Alert,
  Chip, Divider, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button,
} from '@mui/material';
import {
  CheckCircle, Warning, Error, Assessment, TrendingUp, ExpandMore, Print,
} from '@mui/icons-material';
import { openCompositePrintReport } from '../../printReports/appPrintReports';
import { EurocodeText } from '../../utils/eurocodeNotation';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  Filler,
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  PointElement,
  LineElement,
  Filler
);

// Component for enhanced fire design results table
const FireDesignTable = ({ detailedFire, formatNumber }) => {
  if (!detailedFire || Object.keys(detailedFire).length === 0) return null;

  const materialGroups = {
    direction: { bg: '#f1f8e9', border: '#689f38', name: 'Ambient Direction Loading' },
    concrete: { bg: '#e3f2fd', border: '#2196f3', name: 'Concrete' },
    steel: { bg: '#fff3e0', border: '#ff9800', name: 'Steel Tube' },
    rebar: { bg: '#f3e5f5', border: '#9c27b0', name: 'Reinforcement' },
    buckling: { bg: '#fce4ec', border: '#e91e63', name: 'Buckling Parameters' },
    resistance: { bg: '#e8f5e8', border: '#4caf50', name: 'Resistances' }
  };

  const getTooltipText = (param) => {
    const tooltips = {
      util_y_ambient: 'Ambient Y-direction utilization (|My_ed|/My_rd) determining imperfection direction',
      util_z_ambient: 'Ambient Z-direction utilization (|Mz_ed|/Mz_rd) determining imperfection direction',
      imperfection_axis: 'Direction receiving imperfection moment (most heavily loaded)',
      theta_a: 'Steel tube temperature',
      theta_c: 'Concrete temperature',
      theta_s: 'Reinforcement temperature',
      k_y_a: 'Steel yield strength reduction factor',
      phi_a: 'Steel strength reduction factor',
      k_s: 'Reinforcement yield strength reduction factor',
      phi_s: 'Reinforcement strength reduction factor',
      k_c: 'Concrete compressive strength reduction factor',
      phi_c: 'Concrete strength reduction factor',
      N_fi_a_Rd: 'Steel tube axial resistance',
      N_fi_s_Rd: 'Reinforcement axial resistance',
      N_fi_c_Rd: 'Concrete axial resistance',
      N_fi_pl_Rd: 'Plastic axial resistance (sum of components)',
      EI_fi_eff: 'Effective stiffness at elevated temperature',
      N_fi_cr: 'Euler buckling load at elevated temperature',
      lambda_theta: 'Slenderness ratio at elevated temperature',
      chi: 'Buckling reduction factor',
      N_fi_Rd: 'Design buckling resistance',
      M_fi_pl_Rd: 'Plastic moment resistance'
    };
    return tooltips[param] || param;
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
              Fire Design Analysis (EN 1994-1-2 - Finnish NA)
            </TableCell>
            {Object.keys(detailedFire).map(rating => (
              <TableCell key={rating} align="center" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                R{rating} min
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Direction Loading Group */}
          <TableRow sx={{ bgcolor: materialGroups.direction.bg }}>
            <TableCell sx={{ fontWeight: 'bold', borderRight: `2px solid ${materialGroups.direction.border}` }}>
              {materialGroups.direction.name}
            </TableCell>
            {Object.keys(detailedFire).map(rating => {
              const results = detailedFire[rating];
              return (
                <TableCell key={rating} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }} component="div">
                      |M<sub>y,Ed</sub>|/M<sub>y,Rd</sub>: {formatNumber(results.util_y_ambient || 0, 3)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }} component="div">
                      |M<sub>z,Ed</sub>|/M<sub>z,Rd</sub>: {formatNumber(results.util_z_ambient || 0, 3)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                      Imperfection: {results.imperfection_axis || 'Y'}
                    </Typography>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>

          {/* Concrete Group */}
          <TableRow sx={{ bgcolor: materialGroups.concrete.bg }}>
            <TableCell sx={{ fontWeight: 'bold', borderRight: `2px solid ${materialGroups.concrete.border}` }}>
              {materialGroups.concrete.name}
            </TableCell>
            {Object.keys(detailedFire).map(rating => {
              const results = detailedFire[rating];
              return (
                <TableCell key={rating} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      T: {formatNumber(results.theta_c || 0)}°C
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      k: {formatNumber(results.k_c || 0, 3)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      φ: {formatNumber(results.phi_c || 0, 2)}
                    </Typography>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>

          {/* Steel Group */}
          <TableRow sx={{ bgcolor: materialGroups.steel.bg }}>
            <TableCell sx={{ fontWeight: 'bold', borderRight: `2px solid ${materialGroups.steel.border}` }}>
              {materialGroups.steel.name}
            </TableCell>
            {Object.keys(detailedFire).map(rating => {
              const results = detailedFire[rating];
              return (
                <TableCell key={rating} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      T: {formatNumber(results.theta_a || 0)}°C
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      k: {formatNumber(results.k_y_a || 0, 3)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      φ: {formatNumber(results.phi_a || 0, 2)}
                    </Typography>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>

          {/* Rebar Group */}
          <TableRow sx={{ bgcolor: materialGroups.rebar.bg }}>
            <TableCell sx={{ fontWeight: 'bold', borderRight: `2px solid ${materialGroups.rebar.border}` }}>
              {materialGroups.rebar.name}
            </TableCell>
            {Object.keys(detailedFire).map(rating => {
              const results = detailedFire[rating];
              return (
                <TableCell key={rating} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      {results.theta_sc != null || results.theta_sm != null
                        ? [
                            results.theta_sc != null ? `corner ${formatNumber(results.theta_sc)}°C` : null,
                            results.theta_sm != null ? `side ${formatNumber(results.theta_sm)}°C` : null,
                          ].filter(Boolean).join(' · ')
                        : `T: ${formatNumber(results.theta_s || 0)}°C`}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      k: {formatNumber(results.k_s || 0, 3)}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      φ: {formatNumber(results.phi_s || 0, 2)}
                    </Typography>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>

          {/* Buckling Parameters Group */}
          <TableRow sx={{ bgcolor: materialGroups.buckling.bg }}>
            <TableCell sx={{ fontWeight: 'bold', borderRight: `2px solid ${materialGroups.buckling.border}` }}>
              {materialGroups.buckling.name}
            </TableCell>
            {Object.keys(detailedFire).map(rating => {
              const results = detailedFire[rating];
              const ax = results.critical_axis === 'z' ? 'z' : 'y';
              const lambdaT = results[`lambda_theta_${ax}`] ?? results.lambda_theta_y ?? 0;
              const chiF = results[`chi_${ax}`] ?? results.chi_y ?? 0;
              return (
                <TableCell key={rating} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.7rem' }}
                      title={getTooltipText('lambda_theta')}
                      component="div"
                    >
                      λ<sub>θ</sub> ({ax}): {formatNumber(lambdaT, 2)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.7rem' }}
                      title={getTooltipText('chi')}
                    >
                      χ ({ax}): {formatNumber(chiF, 3)}
                    </Typography>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>

          {/* Resistance Group */}
          <TableRow sx={{ bgcolor: materialGroups.resistance.bg }}>
            <TableCell sx={{ fontWeight: 'bold', borderRight: `2px solid ${materialGroups.resistance.border}` }}>
              {materialGroups.resistance.name}
            </TableCell>
            {Object.keys(detailedFire).map(rating => {
              const results = detailedFire[rating];
              return (
                <TableCell key={rating} align="center">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
                      title={getTooltipText('N_fi_pl_Rd')}
                    >
                      N_pl: {formatNumber(results.N_fi_pl_Rd / 1000 || 0)} kN
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: '0.7rem' }}
                      title={getTooltipText('N_fi_Rd')}
                    >
                      N_Rd: {formatNumber(results.N_fi_Rd / 1000 || 0)} kN
                    </Typography>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// Component for rendering M-N interaction charts
// Component for ULS M-N interaction charts
const MNInteractionChart = ({ data, title, sectionDetails, detailedAmbient }) => {
  if (!data || (!data.y_axis && !data.z_axis)) {
    return (
      <Box sx={{ textAlign: 'center', p: 4, border: '1px solid #ddd', borderRadius: '4px' }}>
        <Typography variant="body2" color="text.secondary">
          No interaction data available
        </Typography>
      </Box>
    );
  }

  // Determine if section is doubly symmetric
  const isDoublySymmetric = sectionDetails?.type === 'CHS' ||
                           (sectionDetails?.type === 'RHS' && Math.abs((sectionDetails?.width || 0) - (sectionDetails?.height || 0)) < 1);

  // Get imperfection axis to determine which axis is critical
  const imperfectionAxis = detailedAmbient?.imperfection_axis || 'y';

  // Handle case where only y_axis or z_axis data exists
  const hasYAxis = data.y_axis && data.y_axis.M && data.y_axis.M.length > 0;
  const hasZAxis = data.z_axis && data.z_axis.M && data.z_axis.M.length > 0;

  if (!hasYAxis && !hasZAxis) {
    return (
      <Box sx={{ textAlign: 'center', p: 4, border: '1px solid #ddd', borderRadius: '4px' }}>
        <Typography variant="body2" color="text.secondary">
          Interaction curves not available
        </Typography>
      </Box>
    );
  }

  // Determine which axes to show
  let showYAxis = hasYAxis;
  let showZAxis = hasZAxis;

  if (isDoublySymmetric) {
    // For doubly symmetric sections, show only the critical axis (where imperfections are applied)
    if (imperfectionAxis === 'y') {
      showZAxis = false;
    } else {
      showYAxis = false;
    }
  }
  // For non-symmetric sections, show both axes

  const datasets = [];

  if (showYAxis) {
    const yData = data.y_axis.M.map((m, i) => ({ x: m, y: data.y_axis.N[i] }));

    // Find the maximum N value (pure compression capacity)
    const maxN = Math.max(...data.y_axis.N);

    // Find discontinuity point (where curve transitions from pure compression to compression+bending)
    // Typically this is where the curve starts to bend downward significantly
    let discontinuityIndex = 0;
    const nThreshold = maxN * 0.95; // 95% of max N as threshold
    for (let i = 0; i < yData.length; i++) {
      if (yData[i].y < nThreshold) {
        discontinuityIndex = i;
        break;
      }
    }

    // Create main filled area: from N_max,M=0 down to discontinuity point, then to (0,0)
    const mainFilledData = [
      { x: 0, y: maxN }, // Start from N_max at M=0
      ...yData.slice(0, discontinuityIndex + 1), // Follow curve to discontinuity
      { x: yData[discontinuityIndex]?.x || 0, y: 0 }, // Down to y=0 at discontinuity M
      { x: 0, y: 0 } // Back to origin
    ];

    // Create tip filled area: discontinuity point to end of curve to (maxM,0) to discontinuity
    const tipFilledData = [
      { x: yData[discontinuityIndex]?.x || 0, y: yData[discontinuityIndex]?.y || 0 },
      ...yData.slice(discontinuityIndex),
      { x: Math.max(...data.y_axis.M), y: 0 },
      { x: yData[discontinuityIndex]?.x || 0, y: 0 }
    ];

    datasets.push({
      label: 'Y-Axis Capacity (filled)',
      data: mainFilledData,
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      fill: 'origin',
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 0,
      showLine: false,
    });

    // Add the curve line on top
    datasets.push({
      label: 'Y-Axis Capacity',
      data: yData,
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'transparent',
      fill: false,
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 4,
    });
  }

  if (showZAxis) {
    const zData = data.z_axis.M.map((m, i) => ({ x: m, y: data.z_axis.N[i] }));

    // Find the maximum N value (pure compression capacity)
    const maxN = Math.max(...data.z_axis.N);

    // Find discontinuity point (where curve transitions from pure compression to compression+bending)
    let discontinuityIndex = 0;
    const nThreshold = maxN * 0.95; // 95% of max N as threshold
    for (let i = 0; i < zData.length; i++) {
      if (zData[i].y < nThreshold) {
        discontinuityIndex = i;
        break;
      }
    }

    // Create main filled area: from N_max,M=0 down to discontinuity point, then to (0,0)
    const mainFilledData = [
      { x: 0, y: maxN }, // Start from N_max at M=0
      ...zData.slice(0, discontinuityIndex + 1), // Follow curve to discontinuity
      { x: zData[discontinuityIndex]?.x || 0, y: 0 }, // Down to y=0 at discontinuity M
      { x: 0, y: 0 } // Back to origin
    ];

    // Create tip filled area: discontinuity point to end of curve to (maxM,0) to discontinuity
    const tipFilledData = [
      { x: zData[discontinuityIndex]?.x || 0, y: zData[discontinuityIndex]?.y || 0 },
      ...zData.slice(discontinuityIndex),
      { x: Math.max(...data.z_axis.M), y: 0 },
      { x: zData[discontinuityIndex]?.x || 0, y: 0 }
    ];

    datasets.push({
      label: 'Z-Axis Capacity (filled)',
      data: mainFilledData,
      borderColor: 'rgb(239, 68, 68)',
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      fill: 'origin',
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 0,
      showLine: false,
    });

    // Add the curve line on top
    datasets.push({
      label: 'Z-Axis Capacity',
      data: zData,
      borderColor: 'rgb(239, 68, 68)',
      backgroundColor: 'transparent',
      fill: false,
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 4,
    });
  }

  // Determine max M value for buckling limit line
  let maxM = 0;
  if (showYAxis && data.y_axis.M) maxM = Math.max(maxM, ...data.y_axis.M);
  if (showZAxis && data.z_axis.M) maxM = Math.max(maxM, ...data.z_axis.M);

  const chartData = {
    datasets: [
      ...datasets,
      // Buckling limit line
      ...(data.buckling_limit ? [{
        label: `Buckling Limit (${data.buckling_limit.toFixed(0)} kN)`,
        data: [
          { x: maxM, y: data.buckling_limit },
          { x: 0, y: data.buckling_limit }
        ],
        borderColor: 'rgb(0, 0, 0)',
        borderDash: [5, 5],
        backgroundColor: 'transparent',
        pointRadius: 0,
        fill: false,
      }] : []),
      // Design point with detailed label
      ...(data.design_point ? [{
        label: `Design: N=${data.design_point.N.toFixed(1)}kN, My=${data.design_point.M_y.toFixed(1)}kNm${data.design_point.M_z ? `, Mz=${data.design_point.M_z.toFixed(1)}kNm` : ''}`,
        data: [{
          x: data.design_point.M_y,
          y: data.design_point.N
        }],
        borderColor: 'rgb(0, 0, 0)',
        backgroundColor: 'rgb(0, 0, 0)',
        pointRadius: 8,
        pointHoverRadius: 10,
        pointStyle: 'crossRot',
        showLine: false,
      }] : []),
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: title,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: M = ${context.parsed.x.toFixed(1)} kNm, N = ${context.parsed.y.toFixed(1)} kN`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Moment [kNm]',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Axial Force [kN]',
        },
      },
    },
    elements: {
      point: {
        hoverBorderWidth: 3,
      },
    },
  };

  return (
    <Box sx={{ height: 400, width: '100%' }}>
      <Line data={chartData} options={options} />
      {isDoublySymmetric && (
        <Typography variant="caption" sx={{ mt: 1, textAlign: 'center', display: 'block', color: 'text.secondary' }}>
          Doubly symmetric section - showing {imperfectionAxis.toUpperCase()}-axis (critical)
        </Typography>
      )}
    </Box>
  );
};

// Component for unified Fire + Ambient M-N interaction charts
const FireAmbientInteractionChart = ({ data, title, sectionDetails }) => {
  if (!data || !data.ambient_curve) {
    return (
      <Box sx={{ textAlign: 'center', p: 4, border: '1px solid #ddd', borderRadius: '4px' }}>
        <Typography variant="body2" color="text.secondary">
          No fire design data available
        </Typography>
      </Box>
    );
  }

  const datasets = [];

  // Determine if section is doubly symmetric
  const isDoublySymmetric = sectionDetails?.type === 'CHS' ||
                           (sectionDetails?.type === 'RHS' && Math.abs((sectionDetails?.width || 0) - (sectionDetails?.height || 0)) < 1);

  // Ambient curve (filled area) - use same logic as ULS curves
  if (data.ambient_curve.M && data.ambient_curve.M.length > 0) {
    const ambientData = data.ambient_curve.M.map((m, i) => ({ x: m, y: data.ambient_curve.N[i] }));

    // Find the maximum N value (pure compression capacity)
    const maxN = Math.max(...data.ambient_curve.N);

    // Find discontinuity point (where curve transitions from pure compression to compression+bending)
    let discontinuityIndex = 0;
    const nThreshold = maxN * 0.95; // 95% of max N as threshold
    for (let i = 0; i < ambientData.length; i++) {
      if (ambientData[i].y < nThreshold) {
        discontinuityIndex = i;
        break;
      }
    }

    // Create main filled area: from N_max,M=0 down to discontinuity point, then to (0,0)
    const mainFilledData = [
      { x: 0, y: maxN }, // Start from N_max at M=0
      ...ambientData.slice(0, discontinuityIndex + 1), // Follow curve to discontinuity
      { x: ambientData[discontinuityIndex]?.x || 0, y: 0 }, // Down to y=0 at discontinuity M
      { x: 0, y: 0 } // Back to origin
    ];

    // Create tip filled area: discontinuity point to end of curve to (maxM,0) to discontinuity
    const tipFilledData = [
      { x: ambientData[discontinuityIndex]?.x || 0, y: ambientData[discontinuityIndex]?.y || 0 },
      ...ambientData.slice(discontinuityIndex),
      { x: Math.max(...data.ambient_curve.M), y: 0 },
      { x: ambientData[discontinuityIndex]?.x || 0, y: 0 }
    ];

    datasets.push({
      label: 'Safe region (Section)',
      data: mainFilledData,
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      fill: 'origin',
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 0,
      showLine: false,
    });

    // Add the actual curve line on top
    const axisLabel = isDoublySymmetric ? `(doubly symmetric)` : `(${data.ambient_curve.axis.toUpperCase()}-axis)`;
    datasets.push({
      label: `Ambient section capacity ${axisLabel}`,
      data: ambientData,
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'transparent',
      borderWidth: 2.5,
      fill: false,
      tension: 0.1,
      pointRadius: 0,
      pointHoverRadius: 4,
    });
  }

  // Ambient buckling limit (horizontal dashed line)
  if (data.ambient_curve.buckling_limit) {
    const maxM = data.ambient_curve.M ? Math.max(...data.ambient_curve.M) : 100;
    const minM = data.ambient_curve.M ? Math.min(...data.ambient_curve.M) : 0;
    datasets.push({
      label: `Ambient buckling limit (${data.ambient_curve.buckling_limit.toFixed(0)} kN)`,
      data: [
        { x: maxM, y: data.ambient_curve.buckling_limit },
        { x: minM, y: data.ambient_curve.buckling_limit }
      ],
      borderColor: 'rgb(0, 0, 0)',
      borderDash: [8, 4],
      backgroundColor: 'transparent',
      pointRadius: 0,
      fill: false,
    });
  }

  // Fire curves for each rating
  const fireColors = { 30: '#FFA500', 60: '#FF6600', 90: '#FF3300', 120: '#CC0000' };

  data.fire_curves.forEach((fireCurve) => {
    if (fireCurve.M && fireCurve.M.length > 0) {
      datasets.push({
        label: `R${fireCurve.rating} limit (NCCI)`,
        data: fireCurve.M.map((m, i) => ({ x: m, y: fireCurve.N[i] })),
        borderColor: fireColors[fireCurve.rating] || '#999999',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        fill: false,
        tension: 0.1,
        pointRadius: 0,
        pointHoverRadius: 4,
      });
    }
  });

  // Fire limits (buckling and moment)
  data.fire_limits.forEach((limit) => {
    const color = fireColors[limit.rating] || '#999999';

    // Fire buckling limit (horizontal dotted line)
    if (limit.buckling_limit) {
      datasets.push({
        label: `R${limit.rating} buckling limit (${limit.buckling_limit.toFixed(0)} kN)`,
        data: [
          { x: 0, y: limit.buckling_limit },
          { x: Math.max(...(data.ambient_curve.M || [100])), y: limit.buckling_limit }
        ],
        borderColor: color,
        borderDash: [2, 2],
        backgroundColor: 'transparent',
        pointRadius: 0,
        fill: false,
      });
    }

    // Fire moment limit (vertical dash-dot line)
    if (limit.moment_limit) {
      datasets.push({
        label: `M_fi,pl,Rd (${limit.moment_limit.toFixed(0)} kNm)`,
        data: [
          { x: limit.moment_limit, y: 0 },
          { x: limit.moment_limit, y: limit.buckling_limit || Math.max(...(data.ambient_curve.N || [1000])) }
        ],
        borderColor: color,
        borderDash: [8, 2, 2, 2],
        backgroundColor: 'transparent',
        pointRadius: 0,
        fill: false,
      });

      // Create shaded region following NCCI fire curve
      if (limit.buckling_limit) {
        // Find the corresponding fire curve data (already clipped at intersection)
        const fireCurve = data.fire_curves.find(fc => fc.rating === limit.rating);
        if (fireCurve && fireCurve.M && fireCurve.M.length > 0) {
          const fireCurveData = fireCurve.M.map((m, i) => ({ x: m, y: fireCurve.N[i] }));

          // Create polygon: start from (0, buckling_limit), follow fire curve, then down to (0,0)
          const fireShadingData = [
            { x: 0, y: limit.buckling_limit }, // Start at buckling limit
            ...fireCurveData, // Follow the clipped NCCI fire curve
            { x: limit.moment_limit, y: 0 }, // Down to y=0 at M_fi_pl_rd
            { x: 0, y: 0 } // Back to origin
          ];

          datasets.push({
            label: `R${limit.rating} fire capacity region`,
            data: fireShadingData,
            borderColor: color,
            backgroundColor: `${color}20`, // Add transparency
            fill: true,
            tension: 0.1,
            pointRadius: 0,
            pointHoverRadius: 0,
            showLine: false,
          });
        }
      }
    }
  });

  // Design points
  if (data.design_points) {
    // Ambient design point
    if (data.design_points.ambient) {
      datasets.push({
        label: `Ambient: M_y+M_z = ${data.design_points.ambient.M_y.toFixed(0)}+${data.design_points.ambient.M_z.toFixed(0)} = ${data.design_points.ambient.M_total.toFixed(0)} kNm`,
        data: [{
          x: data.design_points.ambient.M_total,
          y: data.design_points.ambient.N
        }],
        borderColor: 'rgb(0, 0, 0)',
        backgroundColor: 'rgb(0, 0, 0)',
        pointRadius: 10,
        pointHoverRadius: 12,
        pointStyle: 'crossRot',
        showLine: false,
      });
    }

    // Fire design point - EN 1994-1-2 combined moment approach
    if (data.design_points.fire) {
      const firePoint = data.design_points.fire;
      // For symmetric sections: My + Mz + M_imp, for non-symmetric: max(My, Mz) + M_imp
      const isSymmetric = sectionDetails?.type === 'CHS' ||
                         (sectionDetails?.type === 'RHS' && Math.abs((sectionDetails?.width || 0) - (sectionDetails?.height || 0)) < 1);

      let labelText;
      if (isSymmetric) {
        labelText = `Fire: M_y+M_z+M_imp = ${firePoint.M_y.toFixed(0)}+${firePoint.M_z.toFixed(0)}+${firePoint.M_imp.toFixed(0)} = ${firePoint.M_total.toFixed(1)} kNm`;
      } else {
        const maxMoment = Math.max(Math.abs(firePoint.M_y), Math.abs(firePoint.M_z));
        labelText = `Fire: M_max+M_imp = ${maxMoment.toFixed(0)}+${firePoint.M_imp.toFixed(0)} = ${firePoint.M_total.toFixed(1)} kNm`;
      }

      datasets.push({
        label: labelText,
        data: [{
          x: firePoint.M_total,
          y: firePoint.N
        }],
        borderColor: 'rgb(255, 0, 0)',
        backgroundColor: 'rgb(255, 0, 0)',
        pointRadius: 10,
        pointHoverRadius: 12,
        pointStyle: 'crossRot',
        showLine: false,
      });
    }
  }

  const chartData = { datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 12,
          font: {
            size: 11
          }
        }
      },
      title: {
        display: true,
        text: title,
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.dataset.label}: M = ${context.parsed.x.toFixed(1)} kNm, N = ${context.parsed.y.toFixed(1)} kN`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Moment M [kNm]',
        },
      },
      y: {
        title: {
          display: true,
          text: 'Axial force N [kN]',
        },
      },
    },
    elements: {
      point: {
        hoverBorderWidth: 3,
      },
    },
  };

  return (
    <Box sx={{ height: 500, width: '100%' }}>
      <Line data={chartData} options={options} />
      {data.design_points && (
        <Box sx={{ mt: 1, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
            η_fi = {data.eta_fi}, L_fi = {data.L_fi.toFixed(0)} mm
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const CompositeColumnResults = ({ results }) => {
  const handlePrintReport = useCallback(() => {
    if (results) openCompositePrintReport(results, {});
  }, [results]);

  if (!results) return null;

  // Format numbers with space grouping
  const formatNumber = (value, fractionDigits = 0) => {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }).replace(/,/g, ' ');
  };

  // Handle both response structures: wrapped {input_parameters, results} or direct results
  let input_parameters, analysisResults;

  if (results && results.input_parameters && results.results) {
    ({ input_parameters, results: analysisResults } = results);
  } else if (results && results.capacities) {
    analysisResults = results;
    input_parameters = results.input_parameters || {};
  } else {
    analysisResults = {};
    input_parameters = {};
  }

  // Extract key results from the analysis
  const getUtilizationColor = (utilization) => {
    if (utilization <= 0.8) return 'success';
    if (utilization <= 1.0) return 'warning';
    return 'error';
  };

  const getUtilizationIcon = (utilization) => {
    if (utilization <= 0.8) return <CheckCircle />;
    if (utilization <= 1.0) return <Warning />;
    return <Error />;
  };

  // Extract analysis data
  const capacities = analysisResults?.capacities || {};
  const utilization = analysisResults?.utilization || {};
  const sectionProps = analysisResults?.section_properties || {};
  const detailedAmbient = analysisResults?.detailed_ambient || {};
  const detailedFire = analysisResults?.detailed_fire || {};
  const selfWeight = analysisResults?.self_weight || {};
  const plotDataULS = analysisResults?.plot_data_uls;
  const plotDataFire = analysisResults?.plot_data_fire;
  const plotDataFireTemperatureField = analysisResults?.plot_data_fire_temperature_field;
  const crossSectionData = analysisResults?.cross_section_data;
  const warnings = analysisResults?.warnings || [];
  const sectionDetails = analysisResults?.section_properties?.section_details || {};
  const loadIntroduction = analysisResults?.section_properties?.load_introduction || {};
  const u_s = analysisResults?.section_properties?.u_s;
  const rebarPositions = analysisResults?.section_properties?.rebar_positions || [];
  const rebarLayout = analysisResults?.section_properties?.rebar_layout;
  const loadsFromApi = analysisResults?.loads || {};
  const N_Ed_design_kN = loadsFromApi.N_Ed != null ? loadsFromApi.N_Ed : (
    (input_parameters?.N_Ed || 0) + (selfWeight?.N_self || 0) / 1000
  );
  const N_Ed_ext_kN = loadsFromApi.N_Ed_ext != null ? loadsFromApi.N_Ed_ext : (input_parameters?.N_Ed || 0);
  const L_fi_mm = plotDataFire?.L_fi != null
    ? plotDataFire.L_fi
    : (input_parameters?.comcol_length || 0) * (input_parameters?.k_L_modifier ?? 1);

  return (
    <div>
      <Typography variant="h4" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Assessment />
        Analysis Results
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<Print />} onClick={handlePrintReport}>
          Print report (PDF)
        </Button>
      </Box>

      {/* Status Alert */}
      <Alert
        severity="success"
        sx={{ mb: 3 }}
        icon={<CheckCircle />}
      >
        Composite column analysis completed successfully
      </Alert>

      {/* Case Details Header */}
      <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Case Details
          </Typography>
          <Typography variant="body1">
            {input_parameters?.column_type?.replace('_', ' ').toUpperCase()} - {input_parameters?.section_type} - {input_parameters?.section_name} - L: {formatNumber(input_parameters?.comcol_length || 0)} mm - Fz: {formatNumber((input_parameters?.N_Ed || 0))} kN, My: {formatNumber((input_parameters?.M_Ed_y || 0))} kNm, Mz: {formatNumber((input_parameters?.M_Ed_z || 0))} kNm
          </Typography>
        </CardContent>
      </Card>

      {/* Warnings */}
      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Warnings</Typography>
          {warnings.map((warning, index) => (
            <Typography key={index} variant="body2">• {warning}</Typography>
          ))}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Results Content */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Capacity Assessment" />
            <CardContent>
              <Grid container spacing={3}>
                {/* ULS Capacities */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>Ultimate Limit State Capacities</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="h5" color="primary">
                        {formatNumber(capacities.compression || 0)} kN
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        Compression Capacity (<EurocodeText>N_pl,Rd</EurocodeText>)
                      </Typography>
                      <Chip
                        size="small"
                        icon={getUtilizationIcon(utilization.compression)}
                        label={`${(utilization.compression * 100 || 0).toFixed(1)}%`}
                        color={getUtilizationColor(utilization.compression)}
                        sx={{ mt: 1 }}
                      />
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Typography variant="h5" color="primary">
                        {formatNumber(capacities.buckling || 0)} kN
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        Buckling Capacity (<EurocodeText>N_b,Rd</EurocodeText>)
                      </Typography>
                      <Chip
                        size="small"
                        icon={getUtilizationIcon(utilization.buckling)}
                        label={`${(utilization.buckling * 100 || 0).toFixed(1)}%`}
                        color={getUtilizationColor(utilization.buckling)}
                        sx={{ mt: 1 }}
                      />
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Typography variant="h5" color="primary">
                        {formatNumber(capacities.tension || 0)} kN
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        Tension Capacity (<EurocodeText>N_pl,Rk</EurocodeText>)
                      </Typography>
                    </Grid>

                    <Grid item xs={6} md={3}>
                      <Typography variant="h5" color="primary">
                        {formatNumber(sectionProps.area_steel || 0)} mm²
                      </Typography>
                      <Typography variant="body2" color="text.secondary" component="div">
                        Steel Area (<EurocodeText>A_a</EurocodeText>)
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>

                {/* FIRE Design Capacities */}
                {input_parameters?.fire_design_enabled && detailedFire && Object.keys(detailedFire).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main', mt: 2 }}>Fire Design Capacities</Typography>
                    {Object.entries(detailedFire).map(([rating, results]) => (
                      <Grid container spacing={2} key={rating}>
                        <Grid item xs={6} md={3}>
                          <Typography variant="h5" color="secondary">
                            {formatNumber(results.N_fi_Rd / 1000 || 0)} kN
                          </Typography>
                          <Typography variant="body2" color="text.secondary" component="div">
                            Interaction Capacity (<EurocodeText>N_fi,Rd</EurocodeText>(e<sub>fi</sub>))
                          </Typography>
                          <Chip
                            size="small"
                            icon={results.pass ? <CheckCircle /> : <Error />}
                            label={`Utilization: ${(results.util_fire * 100 || 0).toFixed(1)}%`}
                            color={results.pass ? 'success' : 'error'}
                            sx={{ mt: 1 }}
                          />
                        </Grid>
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                            Fire Rating: R{rating} min
                          </Typography>
                          <Typography variant="body2" sx={{ fontSize: '0.9rem', color: 'text.secondary' }}>
                            η_fi: {input_parameters.load_factor_fi || 0.65}
                          </Typography>
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {/* Input Data Summary — same tabular style as Applied Loads */}
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom sx={{ mt: 4, color: 'grey.700', borderBottom: '2px solid', borderColor: 'grey.700', pb: 1 }}>Input Data Summary</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: '42%' }}>Column length L</TableCell>
                          <TableCell>{formatNumber(input_parameters?.comcol_length || 0)} mm</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Effective length L_eff (ambient)</TableCell>
                          <TableCell>{formatNumber(input_parameters?.comcol_length || 0)} mm</TableCell>
                        </TableRow>
                        {input_parameters?.fire_design_enabled && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Fire system length L_fi</TableCell>
                            <TableCell>
                              {formatNumber(L_fi_mm)} mm (as used in fire M–N data; k_L × L in analysis)
                            </TableCell>
                          </TableRow>
                        )}
                        {input_parameters?.fire_design_enabled && (
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Fire temperature model</TableCell>
                            <TableCell>
                              {input_parameters?.fire_temperature_source === 'thermal_fem_2d'
                                ? '2D transient FEM (ISO 834)'
                                : 'NCCI tables'}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Rebar cover to bar centre u_s</TableCell>
                          <TableCell>{formatNumber(u_s || 0)} mm</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>N_Ed,ext</TableCell>
                          <TableCell>{formatNumber(N_Ed_ext_kN)} kN</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Self-weight mass</TableCell>
                          <TableCell>
                            m_s = {formatNumber(selfWeight.m_steel || 0, 1)} kg, m_rebar = {formatNumber(selfWeight.m_reinf || 0, 1)} kg,
                            m_c = {formatNumber(selfWeight.m_concrete || 0, 1)} kg, m_tot = {formatNumber(selfWeight.m_total || 0, 1)} kg
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>N_self</TableCell>
                          <TableCell>{formatNumber((selfWeight?.N_self || 0) / 1000, 1)} kN</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>N_Ed (total, incl. self-weight)</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>{formatNumber(N_Ed_design_kN)} kN</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>M_Ed,y</TableCell>
                          <TableCell>{formatNumber(input_parameters?.M_Ed_y || loadsFromApi.M_Ed_y || 0)} kNm</TableCell>
                        </TableRow>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>M_Ed,z</TableCell>
                          <TableCell>{formatNumber(input_parameters?.M_Ed_z || loadsFromApi.M_Ed_z || 0)} kNm</TableCell>
                        </TableRow>
                        {loadIntroduction?.concrete_share != null && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Axial stiffness share (connection zone)</TableCell>
                            <TableCell>
                              Steel tube {(loadIntroduction.steel_tube_share * 100 || 0).toFixed(1)}% (
                              {formatNumber(loadIntroduction.V_to_steel_tube_kN || 0, 1)} kN of N_Ed);
                              concrete + rebar bundle {(loadIntroduction.concrete_share * 100 || 0).toFixed(1)}% (
                              {formatNumber(loadIntroduction.V_to_concrete_kN || 0, 1)} kN)
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                {loadIntroduction.note_concrete_bundle || ''}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {input_parameters?.use_advanced_loads && input_parameters?.linear_combination && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.contrastText', mb: 1 }}>
                        EN 1990 load combination
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'info.contrastText', mb: 1 }}>
                        {input_parameters.combination_description || 'EN 1990 combination'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'info.contrastText' }}>
                        N_Ed = {formatNumber(input_parameters.linear_combination.N_Ed || 0)} kN
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'info.contrastText' }}>
                        M_Ed,y = {formatNumber(input_parameters.linear_combination.M_Ed_y || 0)} kNm
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'info.contrastText' }}>
                        M_Ed,z = {formatNumber(input_parameters.linear_combination.M_Ed_z || 0)} kNm
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'info.contrastText', fontSize: '0.75rem', mt: 1 }}>
                        Worst-case combination from load cases (EN 1990 ψ). K_FI = {input_parameters?.consequence_class === 'CC2' ? '1.0' : '1.1'}.
                      </Typography>
                    </Box>
                  )}
                </Grid>

                {/* Cross-Section Details */}
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom sx={{ mt: 4, color: 'info.main', borderBottom: '2px solid', borderColor: 'info.main', pb: 1 }}>Cross-Section Details</Typography>

                  {/* Cross-Section Plot */}
                  {crossSectionData && (
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Typography variant="h6" gutterBottom>Cross-Section Geometry</Typography>
                      <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <img
                          src={`data:image/png;base64,${crossSectionData}`}
                          alt="Cross-Section"
                          style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </Box>
                    </Grid>
                  )}

                  {/* Section Properties */}
                  <Grid container spacing={3} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>Section Properties</Typography>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        {sectionDetails.type === 'CHS' && (
                          <>
                            <Typography variant="body2">Outer Diameter (D): {formatNumber(sectionDetails.outer_diameter || 0)} mm</Typography>
                            <Typography variant="body2">Wall Thickness (t): {formatNumber(sectionDetails.wall_thickness || 0)} mm</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>D/t Ratio: {formatNumber(sectionDetails.d_t_ratio || 0, 1)}</Typography>
                          </>
                        )}
                        {sectionDetails.type === 'RHS' && (
                          <>
                            <Typography variant="body2">Height (H): {formatNumber(sectionDetails.height || 0)} mm</Typography>
                            <Typography variant="body2">Width (B): {formatNumber(sectionDetails.width || 0)} mm</Typography>
                            <Typography variant="body2">Wall Thickness (t): {formatNumber(sectionDetails.wall_thickness || 0)} mm</Typography>
                            <Typography variant="body2">Corner Radius (r): {formatNumber(sectionDetails.corner_radius || 0)} mm</Typography>
                          </>
                        )}
                        <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>u_s (rebar cover to center): {formatNumber(u_s || 0)} mm</Typography>
                        {rebarLayout?.u_s_by_role && Object.keys(rebarLayout.u_s_by_role).length > 0 && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Embedment by role
                            {rebarLayout.fire_rating ? ` (sized with R${rebarLayout.fire_rating})` : ''}:{' '}
                            {Object.entries(rebarLayout.u_s_by_role).map(([role, value]) => (
                              `${role} ${formatNumber(value, 1)} mm`
                            )).join(', ')}
                          </Typography>
                        )}
                        {rebarLayout?.optimized && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Layout: {rebarLayout.adopted ? rebarLayout.symmetry : 'pattern'} symmetry.
                            {' '}Utilization {formatNumber(rebarLayout.utilization, 3)}
                            {rebarLayout.baseline_utilization != null
                              ? ` (equal-spacing baseline ${formatNumber(rebarLayout.baseline_utilization, 3)})`
                              : ''}.
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom>Areas & Rebar Ratio</Typography>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2">Steel Area (A_a): {formatNumber(sectionProps.area_steel || 0)} mm²</Typography>
                        <Typography variant="body2">Concrete Area (A_c): {formatNumber(sectionProps.area_concrete || 0)} mm²</Typography>
                        <Typography variant="body2">Rebar Area (A_s): {formatNumber(sectionProps.area_rebar || 0)} mm²</Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          Rebar Ratio (A_s/A_c): {formatNumber(sectionProps.rebar_ratio || 0, 2)}%
                        </Typography>
                        {(sectionProps.rebar_ratio || 0) > 6 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            Rebar ratio exceeds 6% limit!
                          </Alert>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  {rebarPositions.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" gutterBottom>Rebar positions (mm, relative to centroid; x → weak axis, y → strong axis)</Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.100' }}>
                              <TableCell><strong>#</strong></TableCell>
                              <TableCell align="right"><strong>x (mm)</strong></TableCell>
                              <TableCell align="right"><strong>y (mm)</strong></TableCell>
                              <TableCell><strong>Role</strong></TableCell>
                              <TableCell align="right"><strong>u_s (mm)</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rebarPositions.map((b) => (
                              <TableRow key={b.idx}>
                                <TableCell>{b.idx}</TableCell>
                                <TableCell align="right">{formatNumber(b.x, 1)}</TableCell>
                                <TableCell align="right">{formatNumber(b.y, 1)}</TableCell>
                                <TableCell>{b.role || '—'}</TableCell>
                                <TableCell align="right">{b.u_s != null ? formatNumber(b.u_s, 1) : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* Stiffnesses */}
                  <Grid container spacing={3} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>Stiffnesses (EI) for FE Scaling</Typography>
                      <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Y-Axis (Strong):</Typography>
                            <Typography variant="body2">Steel EI_y: {formatNumber((sectionProps.stiffness_steel_y || 0) / 1e9, 3)} ×10⁹ Nmm²</Typography>
                            <Typography variant="body2">Concrete EI_y (K_e=0.6): {formatNumber((sectionProps.stiffness_concrete_y || 0) / 1e9, 3)} ×10⁹ Nmm²</Typography>
                            <Typography variant="body2">Rebar EI_y: {formatNumber((sectionProps.stiffness_rebar_y || 0) / 1e9, 3)} ×10⁹ Nmm²</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total EI_y,eff: {formatNumber((sectionProps.stiffness_total_y || 0) / 1e9, 3)} ×10⁹ Nmm²</Typography>
                            {sectionProps.stiffness_total_y && sectionProps.stiffness_steel_y && (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                Ratio to steel EI_y: {(sectionProps.stiffness_total_y / sectionProps.stiffness_steel_y).toFixed(2)}x
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>Z-Axis (Weak):</Typography>
                            <Typography variant="body2">Steel EI_z: {formatNumber((sectionProps.stiffness_steel_z || 0) / 1e9, 3)} ×10⁹ Nmm²</Typography>
                            <Typography variant="body2">Concrete EI_z (K_e=0.6): {formatNumber((sectionProps.stiffness_concrete_z || 0) / 1e9, 3)} ×10⁹ Nmm²</Typography>
                            <Typography variant="body2">Rebar EI_z: {formatNumber((sectionProps.stiffness_rebar_z || 0) / 1e9, 3)} ×10⁹ Nmm²</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total EI_z,eff: {formatNumber((sectionProps.stiffness_total_z || 0) / 1e9, 3)} ×10⁹ Nmm²</Typography>
                            {sectionProps.stiffness_total_z && sectionProps.stiffness_steel_z && (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                Ratio to steel EI_z: {(sectionProps.stiffness_total_z / sectionProps.stiffness_steel_z).toFixed(2)}x
                              </Typography>
                            )}
                          </Grid>
                        </Grid>

                        {/* EA values */}
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, mt: 2 }}>Axial Stiffnesses (EA):</Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2">Steel EA: {formatNumber((sectionProps.ea_steel || 0) / 1e3, 1)} kN</Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2">Concrete EA (K_e=0.6): {formatNumber((sectionProps.ea_concrete || 0) / 1e3, 1)} kN</Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2">Rebar EA: {formatNumber((sectionProps.ea_rebar || 0) / 1e3, 1)} kN</Typography>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Total EA_eff: {formatNumber((sectionProps.ea_total || 0) / 1e3, 1)} kN</Typography>
                          </Grid>
                          {sectionProps.ea_total && sectionProps.ea_steel && (
                            <Grid item xs={12} md={4}>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                                Ratio to steel EA: {(sectionProps.ea_total / sectionProps.ea_steel).toFixed(2)}x
                              </Typography>
                            </Grid>
                          )}
                        </Grid>

                        <Typography variant="body2" sx={{ mt: 2, fontSize: '0.8rem', color: 'text.secondary' }}>
                          Creep: age = 28 days → φ_t = 1.50 (RH=50%, T=20°C assumed)
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Loads Section — tabular layout aligned with Input Data Summary */}
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom sx={{ mt: 4, color: 'warning.main', borderBottom: '2px solid', borderColor: 'warning.main', pb: 1 }}>Applied Loads & Design Values</Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="h6" gutterBottom color="primary">ULS design actions</Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', width: '45%' }}>N_Ed,ext</TableCell>
                              <TableCell>{formatNumber(N_Ed_ext_kN)} kN</TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>N_Ed (total)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>{formatNumber(N_Ed_design_kN)} kN</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>M_Ed,y</TableCell>
                              <TableCell>{formatNumber(input_parameters?.M_Ed_y || loadsFromApi.M_Ed_y || 0)} kNm</TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>M_Ed,z</TableCell>
                              <TableCell>{formatNumber(input_parameters?.M_Ed_z || loadsFromApi.M_Ed_z || 0)} kNm</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>M_II,y (incl. 2nd order)</TableCell>
                              <TableCell>{formatNumber((detailedAmbient?.M_II_y || 0) / 1e6, 3)} kNm</TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>M_II,z (incl. 2nd order)</TableCell>
                              <TableCell>{formatNumber((detailedAmbient?.M_II_z || 0) / 1e6, 3)} kNm</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>

                    {input_parameters?.fire_design_enabled && detailedFire && Object.keys(detailedFire).length > 0 && (
                      <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom color="secondary">
                          Fire design actions (R{Object.keys(detailedFire)[0]})
                        </Typography>
                        {Object.entries(detailedFire).map(([rating, fr]) => {
                          const nFi = fr.N_fi_Ed_kN != null ? fr.N_fi_Ed_kN : (fr.N_fi_Ed != null ? fr.N_fi_Ed / 1000 : 0);
                          const myFi = fr.M_fi_Ed_y_kNm != null ? fr.M_fi_Ed_y_kNm : (fr.M_fi_Ed_y != null ? fr.M_fi_Ed_y / 1e6 : 0);
                          const mzFi = fr.M_fi_Ed_z_kNm != null ? fr.M_fi_Ed_z_kNm : (fr.M_fi_Ed_z != null ? fr.M_fi_Ed_z / 1e6 : 0);
                          return (
                            <TableContainer key={rating} component={Paper} variant="outlined" sx={{ mb: rating !== Object.keys(detailedFire)[Object.keys(detailedFire).length - 1] ? 2 : 0 }}>
                              <Table size="small">
                                <TableBody>
                                  <TableRow>
                                    <TableCell colSpan={2} sx={{ fontWeight: 'bold', bgcolor: 'secondary.light' }}>
                                      R{rating} — reduced actions (η_fi = {input_parameters?.load_factor_fi || 0.65})
                                    </TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', width: '45%' }}>N_fi,Ed</TableCell>
                                    <TableCell>{formatNumber(nFi)} kN</TableCell>
                                  </TableRow>
                                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>M_fi,Ed,y</TableCell>
                                    <TableCell>{formatNumber(myFi, 3)} kNm</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>M_fi,Ed,z</TableCell>
                                    <TableCell>{formatNumber(mzFi, 3)} kNm</TableCell>
                                  </TableRow>
                                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>e_fi (governing, M/N)</TableCell>
                                    <TableCell>{formatNumber(fr.e_fi != null ? fr.e_fi : 0, 1)} mm</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </TableContainer>
                          );
                        })}
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                {/* Design vs Capacity Table */}
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom sx={{ mt: 4, color: 'success.main', borderBottom: '2px solid', borderColor: 'success.main', pb: 1 }}>Design Verification Summary</Typography>

                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell><strong>Check</strong></TableCell>
                          <TableCell><strong>EN reference</strong></TableCell>
                          <TableCell align="right"><strong>Design value</strong></TableCell>
                          <TableCell align="right"><strong>Capacity</strong></TableCell>
                          <TableCell align="right"><strong>Utilization (%)</strong></TableCell>
                          <TableCell align="center"><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {/* Compression Check */}
                        <TableRow>
                          <TableCell>Compression (<EurocodeText>N_pl,Rd</EurocodeText>)</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>EN 1994-1-1 §6.7.3 (plastic resistance)</TableCell>
                          <TableCell align="right">{formatNumber(N_Ed_design_kN)} kN</TableCell>
                          <TableCell align="right">{formatNumber(capacities.compression || 0)} kN</TableCell>
                          <TableCell align="right">{((N_Ed_design_kN) / (capacities.compression || 1) * 100).toFixed(1)}%</TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={N_Ed_design_kN / (capacities.compression || 1) <= 1 ? 'PASS' : 'FAIL'}
                                  color={N_Ed_design_kN / (capacities.compression || 1) <= 1 ? 'success' : 'error'} />
                          </TableCell>
                        </TableRow>

                        {/* Buckling Check */}
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell>Buckling (<EurocodeText>N_b,Rd</EurocodeText>)</TableCell>
                          <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>EN 1994-1-1 §6.7.3.3–6.7.3.5</TableCell>
                          <TableCell align="right">{formatNumber(N_Ed_design_kN)} kN</TableCell>
                          <TableCell align="right">{formatNumber(capacities.buckling || 0)} kN</TableCell>
                          <TableCell align="right">{((N_Ed_design_kN) / (capacities.buckling || 1) * 100).toFixed(1)}%</TableCell>
                          <TableCell align="center">
                            <Chip size="small" label={N_Ed_design_kN / (capacities.buckling || 1) <= 1 ? 'PASS' : 'FAIL'}
                                  color={N_Ed_design_kN / (capacities.buckling || 1) <= 1 ? 'success' : 'error'} />
                          </TableCell>
                        </TableRow>

                        {/* Y-axis Bending */}
                        {detailedAmbient && (
                          <TableRow>
                            <TableCell>Y-axis bending (<EurocodeText>M_Rd,y</EurocodeText>)</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>EN 1994-1-1 §6.7.3.4, §6.7.3.6</TableCell>
                            <TableCell align="right">{formatNumber(detailedAmbient.M_II_y / 1e6 || 0, 3)} kNm</TableCell>
                            <TableCell align="right">{formatNumber(detailedAmbient.M_Rd_y / 1e6 || 0, 3)} kNm</TableCell>
                            <TableCell align="right">{detailedAmbient.M_Rd_y > 0 ? (detailedAmbient.M_II_y / detailedAmbient.M_Rd_y * 100).toFixed(1) : '0.0'}%</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={detailedAmbient.M_Rd_y > 0 && detailedAmbient.M_II_y / detailedAmbient.M_Rd_y <= 1 ? 'PASS' : 'FAIL'}
                                    color={detailedAmbient.M_Rd_y > 0 && detailedAmbient.M_II_y / detailedAmbient.M_Rd_y <= 1 ? 'success' : 'error'} />
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Z-axis Bending */}
                        {detailedAmbient && (
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell>Z-axis bending (<EurocodeText>M_Rd,z</EurocodeText>)</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>EN 1994-1-1 §6.7.3.4, §6.7.3.6</TableCell>
                            <TableCell align="right">{formatNumber(detailedAmbient.M_II_z / 1e6 || 0, 3)} kNm</TableCell>
                            <TableCell align="right">{formatNumber(detailedAmbient.M_Rd_z / 1e6 || 0, 3)} kNm</TableCell>
                            <TableCell align="right">{detailedAmbient.M_Rd_z > 0 ? (detailedAmbient.M_II_z / detailedAmbient.M_Rd_z * 100).toFixed(1) : '0.0'}%</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={detailedAmbient.M_Rd_z > 0 && detailedAmbient.M_II_z / detailedAmbient.M_Rd_z <= 1 ? 'PASS' : 'FAIL'}
                                    color={detailedAmbient.M_Rd_z > 0 && detailedAmbient.M_II_z / detailedAmbient.M_Rd_z <= 1 ? 'success' : 'error'} />
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Biaxial Check */}
                        {detailedAmbient && (
                          <TableRow>
                            <TableCell>Biaxial interaction</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>EN 1994-1-1 §6.7.3.7 (6.47)</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">—</TableCell>
                            <TableCell align="right">{(detailedAmbient.util_biaxial * 100 || 0).toFixed(1)}%</TableCell>
                            <TableCell align="center">
                              <Chip size="small" label={detailedAmbient.util_biaxial <= 1 ? 'PASS' : 'FAIL'}
                                    color={detailedAmbient.util_biaxial <= 1 ? 'success' : 'error'} />
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Fire Checks */}
                        {input_parameters?.fire_design_enabled && detailedFire && Object.entries(detailedFire).map(([rating, results]) => {
                          const nFi = results.N_fi_Ed_kN != null ? results.N_fi_Ed_kN : (results.N_fi_Ed != null ? results.N_fi_Ed / 1000 : 0);
                          return (
                            <TableRow key={`fire-${rating}`} sx={{ bgcolor: 'grey.50' }}>
                              <TableCell>Fire R{rating} buckling / eccentric</TableCell>
                              <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>EN 1994-1-2 + Finnish NCCI (hollow tube)</TableCell>
                              <TableCell align="right">{formatNumber(nFi)} kN</TableCell>
                              <TableCell align="right">{formatNumber(results.N_fi_Rd / 1000 || 0)} kN</TableCell>
                              <TableCell align="right">{(results.util_fire * 100 || 0).toFixed(1)}%</TableCell>
                              <TableCell align="center">
                                <Chip size="small" label={results.pass ? 'PASS' : 'FAIL'}
                                      color={results.pass ? 'success' : 'error'} />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                {/* ULS Design Results */}
                {detailedAmbient && Object.keys(detailedAmbient).length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="h5" gutterBottom sx={{ mt: 3, color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.main', pb: 1 }}>ULS Design Analysis (EN 1994-1-1)</Typography>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Second-Order Analysis (§6.7.3)</Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle1" gutterBottom color="primary">Y-Axis (Strong)</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">M_Rd,y:</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {formatNumber(detailedAmbient.M_Rd_y / 1000000 || 0)} kNm
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">M_II,y:</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {formatNumber(detailedAmbient.M_II_y / 1000000 || 0)} kNm
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">k_y:</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {formatNumber(detailedAmbient.k_y || 0, 3)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Utilization:</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {(detailedAmbient.util_y * 100 || 0).toFixed(1)}%
                              </Typography>
                            </Grid>
                          </Grid>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">Imperfections:</Typography>
                            <Typography variant="body2" component="div">
                              e₀,y = {formatNumber(detailedAmbient.e0_y || 0)} mm,
                              M_imp,y = {formatNumber(detailedAmbient.M_imp_y / 1000000 || 0)} kNm
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="subtitle1" gutterBottom color="primary">Z-Axis (Weak)</Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">M_Rd,z:</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {formatNumber(detailedAmbient.M_Rd_z / 1000000 || 0)} kNm
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">M_II,z:</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {formatNumber(detailedAmbient.M_II_z / 1000000 || 0)} kNm
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">k_z:</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {formatNumber(detailedAmbient.k_z || 0, 3)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Utilization:</Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {(detailedAmbient.util_z * 100 || 0).toFixed(1)}%
                              </Typography>
                            </Grid>
                          </Grid>
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">Imperfections:</Typography>
                            <Typography variant="body2">
                              e₀,z = {formatNumber(detailedAmbient.e0_z || 0)} mm,
                              M_imp,z = {formatNumber(detailedAmbient.M_imp_z / 1000000 || 0)} kNm
                            </Typography>
                          </Box>
                        </Card>
                      </Grid>

                      <Grid item xs={12}>
                        <Card variant="outlined" sx={{ p: 2, bgcolor: detailedAmbient.util_biaxial <= 1.0 ? 'success.50' : 'error.50' }}>
                          <Typography variant="subtitle1" gutterBottom>Biaxial Interaction Check</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">
                              Combined Utilization: {(detailedAmbient.util_biaxial * 100 || 0).toFixed(1)}%
                            </Typography>
                            {detailedAmbient.util_biaxial <= 1.0 ?
                              <Chip size="small" label="PASS" color="success" /> :
                              <Chip size="small" label="FAIL" color="error" />
                            }
                          </Box>
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Axial Utilization: {(detailedAmbient.util_axial * 100 || 0).toFixed(1)}%
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            EN 1994-1-1 §6.7.3.7: M_Ed,y/M_Rd,y + M_Ed,z/M_Rd,z ≤ 1.0
                          </Typography>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* ULS M-N Interaction Plot */}
                {plotDataULS && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>ULS Design - M-N Interaction Diagram</Typography>
                    <Box sx={{ mt: 2, border: '1px solid #ddd', borderRadius: '4px', p: 2 }}>
                      <MNInteractionChart
                        data={plotDataULS}
                        title="ULS M-N Interaction Diagram"
                        sectionDetails={sectionDetails}
                        detailedAmbient={detailedAmbient}
                      />
                    </Box>
                  </Grid>
                )}

                {/* FIRE M-N Interaction Plot */}
                {plotDataFire && plotDataFire.ambient_curve && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>Fire Design - M-N Interaction Diagram (Ambient + Fire)</Typography>
                    {plotDataFire.temperature_model === 'thermal_fem_2d' && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Fire resistances use steel, concrete and rebar temperatures from the 2D transient ISO 834 FEM;
                        strength reduction factors φ_a and φ_s for buckling are still taken from NCCI tables.
                      </Typography>
                    )}
                    <Box sx={{ mt: 2, border: '1px solid #ddd', borderRadius: '4px', p: 2 }}>
                      <FireAmbientInteractionChart
                        data={plotDataFire}
                        title="M-N Interaction (Ambient + Fire)"
                        sectionDetails={sectionDetails}
                      />
                    </Box>
                  </Grid>
                )}

                {plotDataFireTemperatureField && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'secondary.main' }}>
                      Fire — cross-section temperature field (2D FEM)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Nodal temperatures at the required fire rating (ISO 834 gas curve). Geometry matches the thermal mesh used in the fire check.
                    </Typography>
                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <img
                        src={`data:image/png;base64,${plotDataFireTemperatureField}`}
                        alt="Fire temperature field on cross-section"
                        style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>

              {/* Detailed Fire Design Analysis Table */}
              {input_parameters?.fire_design_enabled && detailedFire && Object.keys(detailedFire).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="h5" gutterBottom sx={{ mt: 3, color: 'secondary.main', borderBottom: '2px solid', borderColor: 'secondary.main', pb: 1 }}>
                    Detailed Fire Design Analysis (EN 1994-1-2 - Finnish NA)
                  </Typography>
                  <FireDesignTable detailedFire={detailedFire} formatNumber={formatNumber} />
                </Grid>
              )}

              {/* Interface Shear and Load Introduction */}
              <Grid item xs={12}>
                <Typography variant="h5" gutterBottom sx={{ mt: 4, color: 'info.main', borderBottom: '2px solid', borderColor: 'info.main', pb: 1 }}>Interface Shear & Load Introduction (EN 1994-1-1 §6.6)</Typography>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Studs in the connection zone transfer the share of axial force intended to be carried by the concrete core
                    (and reinforcement), based on axial stiffness sharing; φ_t = {formatNumber(loadIntroduction.phi_t ?? 0, 2)} in concrete E_c.
                  </Typography>

                  {loadIntroduction && Object.keys(loadIntroduction).length > 0 ? (
                    <>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        1. Connection zone (L_conn = {formatNumber(loadIntroduction.connection_zone_length || 0)} mm)
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold', width: '48%' }}>Total axial N_Ed (incl. self-weight)</TableCell>
                              <TableCell>{formatNumber(loadIntroduction.total_axial_load_kN ?? loadIntroduction.V_conn_kN ?? 0)} kN</TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Shear to transfer via studs (concrete + rebar bundle)</TableCell>
                              <TableCell sx={{ fontWeight: 'bold' }}>
                                {formatNumber(loadIntroduction.shear_to_studs_kN ?? loadIntroduction.V_to_concrete_kN ?? 0, 1)} kN
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Share carried by steel tube (stiffness)</TableCell>
                              <TableCell>
                                {(loadIntroduction.steel_tube_share * 100 || 0).toFixed(1)}% (
                                {formatNumber(loadIntroduction.V_to_steel_tube_kN ?? 0, 1)} kN)
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Share carried by concrete + rebar (stiffness)</TableCell>
                              <TableCell>
                                {(loadIntroduction.concrete_share * 100 || 0).toFixed(1)}% (
                                {formatNumber(loadIntroduction.V_to_concrete_kN ?? 0, 1)} kN)
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 'bold' }}>Stud layout</TableCell>
                              <TableCell>
                                φ{formatNumber(loadIntroduction.stud_diameter || 0)} mm × {formatNumber(loadIntroduction.stud_length || 0)} mm;
                                P_Rd = {formatNumber(loadIntroduction.stud_capacity_kN ?? loadIntroduction.stud_capacity ?? 0, 1)} kN;
                                n = {formatNumber(loadIntroduction.studs_required || 0, 0)} pcs
                                (provided capacity {formatNumber(loadIntroduction.studs_capacity_kN ?? 0, 1)} kN)
                              </TableCell>
                            </TableRow>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 'bold' }}>Longitudinal / transverse spacing</TableCell>
                              <TableCell>
                                ≥ {formatNumber((loadIntroduction.stud_diameter || 19) * 5, 0)} mm (5φ) /
                                ≥ {formatNumber((loadIntroduction.stud_diameter || 19) * 2.5, 1)} mm (2.5φ); edge ≥ 80 mm
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        2. Outside connection zone (L = {formatNumber(loadIntroduction.outside_zone_length_mm ?? 0)} mm)
                      </Typography>
                      <Typography variant="body2">
                        Interface friction capacity (reference): {formatNumber(loadIntroduction.friction_capacity_kN ?? 0, 1)} kN
                        for load redistribution / transverse shear per §6.7.4.
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">Load introduction data not available.</Typography>
                  )}
                </Card>
              </Grid>
            </CardContent>
          </Card>
        </Grid>


                {/* Technical Details */}
                <Grid item xs={12}>
                  <Card>
                    <CardHeader title="Complete Analysis Results" />
                    <CardContent>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Full technical analysis data from EN 1994-1-1 & EN 1994-1-2 calculations.
                      </Typography>
                      <pre style={{
                        backgroundColor: '#f5f5f5',
                        padding: '10px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        overflow: 'auto',
                        maxHeight: '400px'
                      }}>
                        {JSON.stringify(results, null, 2)}
                      </pre>
                    </CardContent>
                  </Card>
                </Grid>
      </Grid>
    </div>
  );
};

export default CompositeColumnResults;
